import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import {
  fetchSeedOffers,
  createSeedOffer,
  updateSeedOffer,
  deleteSeedOffer,
  fetchCampaigns,
  fetchSeedAccounts,
  type SeedCampaign,
  type SeedAccount,
} from '../../lib/seedService';
import { fetchReferential, type RefItem } from '../../lib/referentialService';
import { logger } from '../../lib/logger';

interface SeedOffer {
  id: string;
  positionName: string;
  sectorCode: string;
  city: string;
  contractType: string;
  status: string;
  seedTag: string;
  employerId: string;
  description: string;
  salaryMin: number | null;
  salaryMax: number | null;
  createdAt: string;
}

const contractTypes = [
  { value: 'cdi', label: 'CDI' },
  { value: 'cdd', label: 'CDD' },
  { value: 'interim', label: 'Intérim' },
  { value: 'apprentissage', label: 'Apprentissage' },
  { value: 'extra', label: 'Extra' },
  { value: 'saisonnier', label: 'Saisonnier' },
];

const SeedOffersPage: React.FC = () => {
  const [offers, setOffers] = useState<SeedOffer[]>([]);
  const [campaigns, setCampaigns] = useState<SeedCampaign[]>([]);
  const [employers, setEmployers] = useState<SeedAccount[]>([]);
  const [sectors, setSectors] = useState<RefItem[]>([]);
  const [positions, setPositions] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    campaignTag: '',
    employerId: '',
    sectorCode: '',
    positionCode: '',
    positionName: '',
    city: '',
    contractType: 'cdi',
    description: '',
    salaryMin: '' as string | number,
    salaryMax: '' as string | number,
    count: 1,
  });
  const [creating, setCreating] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Edit modal
  const [editOffer, setEditOffer] = useState<SeedOffer | null>(null);
  const [editForm, setEditForm] = useState({
    positionName: '',
    city: '',
    contractType: '',
    description: '',
    salaryMin: '' as string | number,
    salaryMax: '' as string | number,
  });

  const load = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      fetchSeedOffers(campaignFilter || undefined),
      fetchCampaigns(),
      fetchReferential('sectors'),
      fetchReferential('positions'),
    ]);

    if (results[0].status === 'fulfilled') setOffers(results[0].value);
    else logger.error('load_seed_offers', 'SeedOffersPage', { error: String(results[0].reason) });

    if (results[1].status === 'fulfilled') setCampaigns(results[1].value.filter((x: SeedCampaign) => x.status === 'active'));
    else logger.error('load_campaigns', 'SeedOffersPage', { error: String(results[1].reason) });

    if (results[2].status === 'fulfilled') setSectors(results[2].value);
    else logger.error('load_sectors', 'SeedOffersPage', { error: String(results[2].reason) });

    if (results[3].status === 'fulfilled') setPositions(results[3].value);
    else logger.error('load_positions', 'SeedOffersPage', { error: String(results[3].reason) });

    setLoading(false);
  };

  useEffect(() => { load(); }, [campaignFilter]);

  useEffect(() => {
    if (form.campaignTag) {
      fetchSeedAccounts(form.campaignTag).then((accs) => {
        setEmployers(accs.filter((a) => a.type === 'employer'));
      });
    }
  }, [form.campaignTag]);

  const filteredPositions = useMemo(() => {
    if (!form.sectorCode) return positions;
    const sector = sectors.find((s) => s.code === form.sectorCode);
    if (!sector) return positions;
    return positions.filter((p) => p.sectorId === sector.id);
  }, [form.sectorCode, positions, sectors]);

  const filtered = useMemo(() => {
    return offers.filter((o) => {
      const matchSearch =
        search === '' ||
        o.positionName.toLowerCase().includes(search.toLowerCase()) ||
        o.city.toLowerCase().includes(search.toLowerCase()) ||
        o.sectorCode.toLowerCase().includes(search.toLowerCase());
      return matchSearch;
    });
  }, [offers, search]);

  const handleCreate = async () => {
    if (!form.campaignTag || !form.employerId) return;
    setCreating(true);
    try {
      for (let i = 0; i < form.count; i++) {
        await createSeedOffer({
          campaignTag: form.campaignTag,
          employerId: form.employerId,
          sectorCode: form.sectorCode,
          positionCode: form.positionCode,
          positionName: form.positionName || form.positionCode,
          city: form.city,
          contractType: form.contractType,
          description: form.description || undefined,
          salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
          salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
        });
      }
      alert(`${form.count} offre(s) créée(s).`);
      setShowCreate(false);
      load();
    } catch (err: any) {
      alert(`Erreur: ${err?.message || JSON.stringify(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (o: SeedOffer) => {
    if (!confirm('Supprimer cette offre seed ?')) return;
    setActionLoading(o.id);
    try {
      await deleteSeedOffer(o.id);
      setOffers((prev) => prev.filter((x) => x.id !== o.id));
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openEdit = (o: SeedOffer) => {
    setEditOffer(o);
    setEditForm({
      positionName: o.positionName,
      city: o.city,
      contractType: o.contractType,
      description: o.description || '',
      salaryMin: o.salaryMin ?? '',
      salaryMax: o.salaryMax ?? '',
    });
  };

  const handleEdit = async () => {
    if (!editOffer) return;
    setActionLoading(editOffer.id);
    try {
      await updateSeedOffer(editOffer.id, {
        positionName: editForm.positionName,
        city: editForm.city,
        contractType: editForm.contractType,
        description: editForm.description,
        salaryMin: editForm.salaryMin ? Number(editForm.salaryMin) : null,
        salaryMax: editForm.salaryMax ? Number(editForm.salaryMax) : null,
      });
      setEditOffer(null);
      load();
    } catch (err) {
      alert(`Erreur: ${(err as any)?.message || err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: Column<SeedOffer>[] = [
    {
      key: 'position',
      header: 'Poste',
      render: (o) => (
        <div>
          <div className="flex items-center gap-2">
            <button onClick={() => openEdit(o)} className="font-medium text-gray-900 hover:text-indigo-600 transition-colors">
              {o.positionName || '—'}
            </button>
            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>
          </div>
          <p className="text-xs text-gray-400">{o.sectorCode}</p>
        </div>
      ),
    },
    {
      key: 'city',
      header: 'Ville',
      render: (o) => <span className="text-gray-600">{o.city || '—'}</span>,
    },
    {
      key: 'contract',
      header: 'Contrat',
      render: (o) => (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700">
          {contractTypes.find((c) => c.value === o.contractType)?.label || o.contractType}
        </span>
      ),
    },
    {
      key: 'salary',
      header: 'Salaire',
      render: (o) => (
        <span className="text-xs text-gray-600">
          {o.salaryMin && o.salaryMax ? `${o.salaryMin}–${o.salaryMax}€` : o.salaryMin ? `${o.salaryMin}€+` : '—'}
        </span>
      ),
    },
    {
      key: 'campaign',
      header: 'Campagne',
      render: (o) => <span className="text-xs text-gray-600 font-mono">{o.seedTag}</span>,
    },
    {
      key: 'createdAt',
      header: 'Créée le',
      render: (o) => <span className="text-gray-500 text-xs">{o.createdAt}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (o) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEdit(o)}
            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Éditer
          </button>
          <button
            onClick={() => handleDelete(o)}
            disabled={actionLoading === o.id}
            className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ];

  const filterSlot = (
    <select
      value={campaignFilter}
      onChange={(e) => setCampaignFilter(e.target.value)}
      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
    >
      <option value="">Toutes les campagnes</option>
      {campaigns.map((c) => (
        <option key={c.id} value={c.name}>{c.name}</option>
      ))}
    </select>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="bg-white rounded-xl border border-purple-200 px-4 py-3 flex items-center gap-3">
          <div>
            <p className="text-xl font-bold text-purple-600">{offers.length}</p>
            <p className="text-xs text-gray-500">Offres seeds</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
        >
          + Créer des offres
        </button>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(o) => o.id}
        searchable
        searchPlaceholder="Rechercher par poste, ville, secteur..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={15}
      />

      {/* Create modal (enriched) */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
              <h3 className="text-lg font-semibold text-gray-900">Créer des offres seeds</h3>
              <p className="text-xs text-gray-500 mt-1">Description et salaire auto-générés si laissés vides</p>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campagne</label>
                  <select value={form.campaignTag} onChange={(e) => setForm({ ...form, campaignTag: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Sélectionner...</option>
                    {campaigns.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recruteur seed</label>
                  <select value={form.employerId} onChange={(e) => setForm({ ...form, employerId: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Sélectionner...</option>
                    {employers.map((e) => <option key={e.userId} value={e.employerProfileId || e.userId}>{e.firstName} {e.lastName} {e.companyName ? `(${e.companyName})` : ''}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secteur</label>
                  <select value={form.sectorCode} onChange={(e) => setForm({ ...form, sectorCode: e.target.value, positionCode: '', positionName: '' })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Sélectionner...</option>
                    {sectors.filter((s) => s.active).map((s) => <option key={s.id} value={s.code}>{s.nameFr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                  <select value={form.positionCode} onChange={(e) => { const pos = filteredPositions.find((p) => p.code === e.target.value); setForm({ ...form, positionCode: e.target.value, positionName: pos?.nameFr || e.target.value }); }} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    <option value="">Sélectionner...</option>
                    {filteredPositions.filter((p) => p.active).map((p) => <option key={p.id} value={p.code}>{p.nameFr}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Paris" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contrat</label>
                  <select value={form.contractType} onChange={(e) => setForm({ ...form, contractType: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {contractTypes.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input type="number" min={1} max={20} value={form.count} onChange={(e) => setForm({ ...form, count: Math.min(20, Math.max(1, Number(e.target.value))) })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel, auto-générée si vide)</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Laisser vide pour générer automatiquement" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire min (€/mois)</label>
                  <input type="number" value={form.salaryMin} onChange={(e) => setForm({ ...form, salaryMin: e.target.value })} placeholder="1800" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire max (€/mois)</label>
                  <input type="number" value={form.salaryMax} onChange={(e) => setForm({ ...form, salaryMax: e.target.value })} placeholder="2500" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleCreate} disabled={!form.campaignTag || !form.employerId || creating} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-40">
                {creating ? 'Création...' : `Créer ${form.count} offre(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editOffer && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setEditOffer(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <h3 className="text-lg font-semibold text-gray-900">Modifier l'offre seed</h3>
              <p className="text-xs text-gray-400 mt-1">{editOffer.sectorCode} · {editOffer.seedTag}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du poste</label>
                  <input type="text" value={editForm.positionName} onChange={(e) => setEditForm({ ...editForm, positionName: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input type="text" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contrat</label>
                <select value={editForm.contractType} onChange={(e) => setEditForm({ ...editForm, contractType: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200">
                  {contractTypes.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire min (€)</label>
                  <input type="number" value={editForm.salaryMin} onChange={(e) => setEditForm({ ...editForm, salaryMin: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire max (€)</label>
                  <input type="number" value={editForm.salaryMax} onChange={(e) => setEditForm({ ...editForm, salaryMax: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setEditOffer(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">Annuler</button>
              <button onClick={handleEdit} disabled={actionLoading === editOffer.id} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-40">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeedOffersPage;
